import * as React from 'react';
import ReactDOM from 'react-dom';
import { UNSAFE_LocationContext } from "react-router-dom";
import { setModalContainer } from "../../helpers/addModal";
import Icon from "../../../Icon";
import ModalContext from "../../helpers/ModalContext";
import { ModalStackItem, ModalStackItemProps, ModalStackOptions } from "../../interfaces/modals";
import * as overflowBody from "../../helpers/overflowBody";
import { useRefValue } from "../../../../helpers/useRefValue";
import { isPromise } from "../../../../helpers/isPromise";
import styles from './ModalContainer.scss';

export type ModalContainerApi = {
  open: <Props extends {}>(
    component: React.LazyExoticComponent<React.ComponentType<ModalStackItemProps<Props>>> | React.ComponentType<ModalStackItemProps<Props>>,
    props: Props,
    options?: ModalStackOptions,
  ) => ModalStackItem<Props>,
  closeAll: () => any,
}

let nextIndex = 1;

type Props = {
  targetElementId?: string
  onOpen?: (item: ModalStackItem<any>) => any,
};

const ModalContainer: React.FC<Props> = (props) => {
  const { targetElementId, onOpen } = props;
  const [items, setItems] = React.useState<ModalStackItem[]>([]);
  const itemsRef = useRefValue(items);

  const routerContext = React.useContext(UNSAFE_LocationContext);
  if (routerContext) {
    React.useEffect(() => {
      closeAll();
    }, [routerContext.location.pathname])
  }

  const open = React.useCallback(<Props extends {}>(
    component: React.LazyExoticComponent<React.ComponentType<ModalStackItemProps<Props>>>,
    props: Props,
    options: ModalStackOptions = {},
  ): ModalStackItem<Props> => {
    let resolve: (value: any) => any | null;
    let reject: (reason?: any) => any | null;
    let rejectPredicate: () => any = null;
    const result = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    const item: ModalStackItem<Props> = {
      component,
      index:         nextIndex++,
      active:        true,
      resolve:       (data: any) => resolve(data),
      reject:        (data?: any) => {
        if (typeof rejectPredicate !== "function") return reject(data);
        try {
          const result = rejectPredicate();
          if (isPromise(result)) {
            return result.then((data: any) => {
              data ? resolve(data) : reject(data);
            }, () => {});
          } else {
            result ? resolve(result) : reject(result);
          }
        } catch (e) {
          console.log(e);
        }
      },
      onBeforeClose: (fn: typeof rejectPredicate) => rejectPredicate = fn,
      result,
      props,
      options,
    };

    overflowBody.activate(item.index);

    const name = options?.name;
    const items = itemsRef.current;
    if (name && items.find(item => item.options && item.options.name === name)) {
      reject();
      return item;
    }

    const close = () => {
      // Если вкладка в бекграунде, requestAnimationFrame не сработает вовремя
      item.active = false;
      setItems((items) => items.map(i => i === item ? { ...item, active: false } : i));
    }
    result.then(close, close);

    requestAnimationFrame(() => {
      setItems((items) => item.active ? ([...items, item]) : items);
    })

    onOpen && onOpen(item);

    return item;
  }, [onOpen]);

  const closeAll = React.useCallback(() => {
    items.forEach(item => {
      if (item.active) item.reject();
    });
  }, [items]);

  const api = React.useRef<ModalContainerApi>();
  api.current = { open, closeAll };

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const items = itemsRef.current;
      if (!items.length) return;

      if (e.keyCode === 27) { // ESC
        e.preventDefault();
        e.stopPropagation();
        const topItem = items[items.length - 1];
        if (topItem.options.closeOnEsc === false) return;
        topItem.reject();
      }
    };

    setModalContainer(api);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      setModalContainer(null);
      window.removeEventListener('keydown', onKeyDown);
    }
  }, []);

  // componentDidCatch(error:Error, errorInfo:React.ErrorInfo)
  // {
  //   try {
  //     openMessage({
  //       color: 'danger',
  //       text:  `Ошибка: ${error}`,
  //     });
  //   } catch (e) {
  //   }
  //   console.error(error);
  //   console.log(errorInfo);
  //
  //   this.closeAll();
  //   // const { items } = this.state;
  //   // this.setState({ items: [] });
  //   // items.forEach(item => item.reject());
  // }

  const onModalClose = React.useCallback((index: number) => {
    setItems((items) => items.filter(item => item.index !== index));
    const item = items.find(i => i.index === index);
    if (item) {
      if (item.options.afterClose) item.options.afterClose();
      overflowBody.deactivate(item.index);
    }
  }, [items]);

  const renderModal = React.useCallback((config: ModalStackItem, i: number) => {
    const { component, index, reject, options } = config;
    // const Component = modalComponents[type];
    const Component = component;
    return (
      <ModalContext.Provider key={index} value={config}>
        <React.Suspense fallback={null}>
          <Component
            {...config}
            onClose={onModalClose}
            isTop={itemsRef.current.length - 1 === i}
          >
            {options?.hideClose
              ? null
              : (
                <button className={styles.close} onClick={reject} tabIndex={-1}>
                  <Icon className={styles.closeIcon} icon={require('../../../../icons/X.svg')} />
                </button>
              )
            }
          </Component>
        </React.Suspense>
      </ModalContext.Provider>
    );
  }, [onModalClose]);

  return ReactDOM.createPortal(
    items.map(renderModal),
    document.getElementById(targetElementId || 'modals'),
  );
}

export default React.memo(ModalContainer);
